"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.DmsSelect = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _material = require("@mui/material");
var _DmsFetch = require("../DmsFetch/DmsFetch");
const _excluded = ["value", "properties", "multiple", "needs_authentication", "url", "options", "name", "label", "onChange", "filter", "sortable", "empty_option"];
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _interopRequireWildcard(e, t) { if ("function" == typeof WeakMap) var r = new WeakMap(), n = new WeakMap(); return (_interopRequireWildcard = function (e, t) { if (!t && e && e.__esModule) return e; var o, i, f = { __proto__: null, default: e }; if (null === e || "object" != typeof e && "function" != typeof e) return f; if (o = t ? n : r) { if (o.has(e)) return o.get(e); o.set(e, f); } for (const t in e) "default" !== t && {}.hasOwnProperty.call(e, t) && ((i = (o = Object.defineProperty) && Object.getOwnPropertyDescriptor(e, t)) && (i.get || i.set) ? o(f, t, i) : f[t] = e[t]); return f; })(e, t); }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], -1 === t.indexOf(o) && {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (-1 !== e.indexOf(n)) continue; t[n] = r[n]; } return t; }
/**
 * DmsSelect component for rendering a material UI text field with select input and dynamic options.
 *
 * @param {Object} props
 * @param {string|number|string[]|number[]} [props.value=[]] - Selected value(s) for the select input
 * @param {Object} [props.properties=null] - Object containing properties for formatting option labels and values
 * @param {boolean} [props.multiple=false] - Whether the select allows multiple selections
 * @param {boolean} [props.needs_authentication=true] - Whether the data fetch requires authentication
 * @param {string} [props.url] - URL for fetching options
 * @param {Array<Object>|Object} [props.options] - Options for the select input, can be an array or an object
 * @param {string} [props.name] - Name attribute for the select input
 * @param {string} [props.label] - Label for the select input
 * @param {Function} [props.onChange] - Callback function for handling value changes
 * @param {Object} [props.props] - Additional props to be spread onto the TextField component
 * @returns {React.ReactElement} The select input element
 */

const DmsSelect = _ref => {
  let {
      value = [],
      properties = null,
      multiple = false,
      needs_authentication = true,
      url,
      options,
      name,
      label,
      onChange,
      filter,
      sortable,
      empty_option
    } = _ref,
    props = _objectWithoutProperties(_ref, _excluded);
  const [items, setItems] = (0, _react.useState)([]);
  const [loading, setLoading] = (0, _react.useState)(false);
  const [error, setError] = (0, _react.useState)(null);
  const [selectedValue, setSelectedValue] = (0, _react.useState)(value);

  // Sync selectedValue when value prop changes
  (0, _react.useEffect)(() => {
    setSelectedValue(value);
  }, [value]);
  const updateItems = async () => {
    setLoading(true);
    setError(null);
    try {
      if (url) {
        // Keep the existing behavior when fetching from URL
        const response = await (0, _DmsFetch.fetchData)(url, "GET", needs_authentication);
        const data = response.data.data.rows || response.data.data;
        if (Array.isArray(data)) {
          setItems(data.map(item => ({
            label: item[properties === null || properties === void 0 ? void 0 : properties.label] || item.name,
            value: item[properties === null || properties === void 0 ? void 0 : properties.value] || item.pk
          })));
        } else {
          throw new Error("Data format is incorrect.");
        }
      } else if (options) {
        // Adjust the mapping when options are provided directly
        const formattedOptions = Array.isArray(options) ? options : Object.entries(options).map(_ref2 => {
          let [key, value] = _ref2;
          return {
            label: value,
            // Display the component name
            value: key // Send the pk to the backend
          };
        });
        setItems(formattedOptions);
      } else {
        setItems([]);
      }
    } catch (error) {
      setError("Failed to load data.");
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };
  const handleOnChange = event => {
    const newValue = event.target.value;
    setSelectedValue(newValue);
    if (onChange) {
      onChange(newValue);
    }
  };
  (0, _react.useEffect)(() => {
    updateItems();
  }, [url, options]);

  // Sync internal state with external value prop
  (0, _react.useEffect)(() => {
    setSelectedValue(value);
  }, [value]);
  return /*#__PURE__*/_react.default.createElement(_material.TextField, _extends({
    fullWidth: true,
    name: name,
    label: label,
    select: true,
    variant: "outlined",
    value: multiple ? Array.isArray(selectedValue) ? selectedValue : [] : selectedValue,
    onChange: handleOnChange,
    SelectProps: {
      multiple: multiple,
      renderValue: multiple ? selected => /*#__PURE__*/_react.default.createElement(_material.Box, {
        display: "flex",
        flexWrap: "wrap",
        gap: 0.5,
        maxHeight: 100,
        overflow: "auto"
      }, selected.map(val => {
        // Try to find a matching item label
        let itemLabel = null;
        let lookupValue = val;
        if (typeof val === 'object' && val !== null) {
          // Try each *_pk field to find one that matches an item
          const fkKeys = Object.keys(val).filter(k => k.endsWith('_pk') && typeof val[k] === 'number');
          for (const fkKey of fkKeys) {
            const foundItem = items.find(item => item.value === val[fkKey]);
            if (foundItem) {
              itemLabel = foundItem.label;
              lookupValue = val[fkKey];
              break;
            }
          }
          // Fallback to pk if no FK matched
          if (!itemLabel && val.pk !== undefined) {
            const foundItem = items.find(item => item.value === val.pk);
            if (foundItem) {
              itemLabel = foundItem.label;
              lookupValue = val.pk;
            }
          }
        } else {
          var _items$find;
          itemLabel = (_items$find = items.find(item => item.value === val)) === null || _items$find === void 0 ? void 0 : _items$find.label;
        }

        // Ensure we never render objects as children - only primitives
        const displayLabel = itemLabel || (loading ? '...' : typeof lookupValue === 'object' ? JSON.stringify(lookupValue) : String(lookupValue));
        return /*#__PURE__*/_react.default.createElement(_material.Chip, {
          key: typeof val === 'object' ? JSON.stringify(val) : val,
          label: displayLabel
        });
      })) : undefined,
      MenuProps: {
        PaperProps: {
          style: {
            maxHeight: 300,
            overflowY: 'auto'
          }
        }
      }
    }
  }, props), loading && !error ? /*#__PURE__*/_react.default.createElement(_material.Box, {
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  }, /*#__PURE__*/_react.default.createElement(_material.CircularProgress, {
    size: 24
  })) : error ? /*#__PURE__*/_react.default.createElement(_material.Alert, {
    severity: "error"
  }, error) : [empty_option && /*#__PURE__*/_react.default.createElement(_material.MenuItem, {
    key: "__empty__",
    value: ""
  }, typeof empty_option === "string" ? empty_option : "—"), ...items.map(option => /*#__PURE__*/_react.default.createElement(_material.MenuItem, {
    key: option.value,
    value: option.value
  }, option.label))]);
};
exports.DmsSelect = DmsSelect;
DmsSelect.propTypes = _objectSpread({
  value: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number, _propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number]))]),
  properties: _propTypes.default.shape({
    label: _propTypes.default.string,
    value: _propTypes.default.string
  }),
  multiple: _propTypes.default.bool,
  needs_authentication: _propTypes.default.bool,
  url: _propTypes.default.string,
  options: _propTypes.default.oneOfType([_propTypes.default.arrayOf(_propTypes.default.shape({
    label: _propTypes.default.string,
    value: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number])
  })), _propTypes.default.objectOf(_propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number]))]),
  name: _propTypes.default.string,
  label: _propTypes.default.string,
  onChange: _propTypes.default.func
}, _material.TextField.propTypes);